import { FC, useContext } from "react";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import { Form, ModalDialog } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { SubstructureMaterial } from "../../../../http/deckDesigner/types/substructureMaterials";
import useDeckSubstructureMaterialsApi from "../../../../http/deckDesigner/useSubstructureMaterials";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface SubstructureMaterialFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
}

interface SubstructureMaterialFormProps {
  substructureMaterial: SubstructureMaterial;
  onClose: () => void;
}

const SubstructureMaterialForm: FC<SubstructureMaterialFormProps> = ({ substructureMaterial, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const substructureMaterialsApi = useDeckSubstructureMaterialsApi();

  const defaultValues: SubstructureMaterialFormBody = {
    name: substructureMaterial.name ?? null,
    nameIt: substructureMaterial.nameIt ?? null,
    nameDe: substructureMaterial.nameDe ?? null,
    nameEs: substructureMaterial.nameEs ?? null,
    nameFr: substructureMaterial.nameFr ?? null,
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: substructureMaterial.image ?? undefined,
    isLoading: false,
  });

  const onSubmit = () => async (formData: SubstructureMaterialFormBody) => {
    try {
      const image = await substructureMaterialsApi.uploadImage(substructureMaterial.code, newImage, removeImage);
      await substructureMaterialsApi.updateDeckSubstructureMaterial(substructureMaterial.code, {
        name: formData.name ?? "",
        nameIt: formData.nameIt || null,
        nameDe: formData.nameDe || null,
        nameEs: formData.nameEs || null,
        nameFr: formData.nameFr || null,
        ...image,
      });

      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.substructureMaterials.singular}`}
    >
      <Form<SubstructureMaterialFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <TranslatableTextField form={form} />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SubstructureMaterialForm;
